import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef } from "react";
import { MaskedLine } from "../components/Motion";
import { LINKS, EXPERTISE } from "../lib/content";

const HERO_STATS = [
  { value: "$120K", label: "saved in annual costs" },
  { value: "40%", label: "faster solution delivery" },
  { value: "7", label: "enterprise deals led" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-16">
      <motion.div style={{ y }} className="absolute inset-0" aria-hidden="true">
        <div className="mesh-glow animate-gradient-shift w-[600px] h-[600px] bg-violet-600/25 -top-40 -left-40" />
        <div className="mesh-glow animate-gradient-shift-2 w-[700px] h-[700px] bg-blue-600/20 top-1/3 -right-52" />
        <div className="mesh-glow w-[400px] h-[400px] bg-indigo-500/15 bottom-0 left-1/3" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <MaskedLine delay={0.1}>
              <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-secondary mb-8">
                Yash Bhatt - AI Strategy · Enterprise Solutions · Analytics
              </p>
            </MaskedLine>

            <h1 className="font-display font-medium tracking-tighter leading-[1.1] pb-2 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
              <MaskedLine delay={0.25}>{"AI strategy that "}</MaskedLine>
              <MaskedLine delay={0.4}>{"survives the "}</MaskedLine>
              <MaskedLine delay={0.55}><span className="text-gradient">CFO meeting</span><span className="text-secondary">.</span></MaskedLine>
            </h1>

            <MaskedLine delay={0.75}>
              <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                I help enterprises and SaaS companies turn AI ambition into production systems
                and defensible ROI - TCO, NPV, and IRR included. Strategy through implementation,
                measured in board-deck numbers.
              </p>
            </MaskedLine>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-12 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={LINKS.calendly} target="_blank" rel="noreferrer"
                data-testid="hero-cta-book"
                className="group inline-flex h-13 items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-opacity duration-200"
              >
                Book a Consultation
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link
                to="/projects"
                data-testid="hero-cta-cases"
                className="group inline-flex h-13 items-center justify-center gap-2 px-8 py-4 rounded-full border border-border text-sm font-semibold hover:border-secondary/60 hover:text-secondary transition-colors duration-200"
              >
                View Case Studies
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block lg:col-span-5"
          >
            <div data-testid="hero-stat-card" className="card-hover rounded-2xl border border-border bg-card/80 glass p-10 relative overflow-hidden">
              <div className="mesh-glow w-64 h-64 bg-violet-600/15 -top-20 -right-20" aria-hidden="true" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-secondary mb-8">Outcomes, not opinions</p>
              <div className="space-y-8 relative">
                {HERO_STATS.map((s) => (
                  <div key={s.value} className="flex items-baseline gap-4">
                    <p className="font-display text-4xl xl:text-5xl font-medium tracking-tighter text-gradient shrink-0">{s.value}</p>
                    <p className="text-sm text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const EMPLOYER_LOGOS = [
  { name: "Axion Ray", img: "/logos/axionray.svg" },
  { name: "Google", img: "/logos/google.png" },
  { name: "Oceaneering International", img: "/logos/oceaneering.png" },
  { name: "S&P Global", img: "/logos/spglobal.png" },
];

const ENGAGEMENT_LOGOS = [
  { name: "Boeing", img: "/logos/boeing.png" },
  { name: "Toyota", img: "/logos/toyota.png" },
  { name: "Kenworth", img: "/logos/kenworth.png" },
  { name: "AGCO", img: "/logos/agco.png" },
  { name: "Titan America", img: "/logos/titanamerica.png" },
  { name: "Autoliv", img: "/logos/autoliv.png" },
  { name: "Google Ads", img: "/logos/googleads.png" },
  { name: "Google Analytics", img: "/logos/googleanalytics.png" },
  { name: "YouTube", img: "/logos/youtube.png" },
  { name: "Collins Aerospace", img: "/logos/collins.webp" },
  { name: "Cummins Inc", img: "/logos/cummins.png" },
  { name: "UIN Footwear", img: "/logos/uin.png" },
  { name: "Mercor", img: "/logos/mercor.png" },
];

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const TrustBar = () => (
  <section data-testid="trust-bar" className="border-y border-border py-14 md:py-16" aria-label="Experience and consulting engagements">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-secondary mb-8" data-testid="trust-bar-heading">Experience Across</p>
      <div className="flex flex-wrap items-center justify-center gap-4 md:gap-5" data-testid="trust-bar-employers">
        {EMPLOYER_LOGOS.map((brand) => (
          <div
            key={brand.name}
            data-testid={`trust-logo-${slug(brand.name)}`}
            className="flex items-center justify-center h-20 w-44 rounded-2xl bg-white border border-border/60 transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_10px_36px_-8px_rgba(139,92,246,0.35)]"
          >
            <img src={brand.img} alt={`${brand.name} logo`} loading="lazy" className="max-h-10 w-auto max-w-[130px] object-contain" />
          </div>
        ))}
      </div>

      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-14 mb-7" data-testid="trust-bar-engagements-heading">Consulting engagements spanning</p>
      <div className="flex flex-wrap items-center justify-center gap-3 md:gap-3.5" data-testid="trust-bar-engagements">
        {ENGAGEMENT_LOGOS.map((brand) => (
          <div
            key={brand.name}
            data-testid={`engagement-logo-${slug(brand.name)}`}
            className="flex items-center justify-center h-14 w-32 rounded-xl bg-white border border-border/60 transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_8px_28px_-8px_rgba(139,92,246,0.3)]"
          >
            <img src={brand.img} alt={`${brand.name} logo`} loading="lazy" className="max-h-7 w-auto max-w-[92px] object-contain" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ExpertiseMarquee = () => (
  <div className="border-y border-border py-6 overflow-hidden bg-muted/40" aria-hidden="true">
    <div className="flex w-max animate-marquee gap-0">
      {[0, 1].map((dup) => (
        <div key={dup} className="flex items-center shrink-0">
          {EXPERTISE.map((item) => (
            <span key={`${dup}-${item}`} className="mx-6 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground whitespace-nowrap flex items-center gap-6">
              {item} <span className="text-secondary">✦</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);
